import { defineStore } from 'pinia';
import { ref } from 'vue';
import axios from 'axios';
import type { GameItems, Filters, Game, GameRaw, GameURL, GameURLResponse, GameRandomURL, GameRandomURLResponse, Items, RandomGameDetails, RandomGameDetailsRaw } from '@/types';
import createQueryString from '@/utils/queryString';
import { gameAdapter } from '@/adapters/adapters';
import { appendItems } from '@/utils/appendItems';
import { getAuthHeaders } from '@/utils/getHeaders';
import { usePlayer } from '@/composables/usePlayer';
import { useCurrenciesStore } from './currencies';

export const useGamesStore = defineStore('games', () => {
    const currenciesStore = useCurrenciesStore();
    const { isPlayer, isPlayerHaveBalance, player } = usePlayer();
    const gameURL = ref<GameURLResponse | null>(null);
    const randomGameURL = ref<GameRandomURLResponse | null>(null);
    const randomGame = ref<RandomGameDetails | null>(null);
    const games = ref<GameItems>({
        items: [],
        total: 0,
        size: 0,
        page: 0,
        pages: 0
    });
    const searchedGames = ref<GameItems>({
        items: [],
        total: 0,
        size: 0,
        page: 0,
        pages: 0
    });
    const gamesByProvider = ref<Record<string, GameItems>>({});
    const searchFlag = ref<boolean>(false);
    const searchName = ref<string>('');
    const loadingSearch = ref<boolean>(false);
    function toResetSearchFlag(): void {
        searchFlag.value = false;
        searchedGames.value = {
            items: [],
            total: 0,
            size: 0,
            page: 0,
            pages: 0
        };
    }
    function toUpdateGameFavoriteStatus(game: Game, action: 'add' | 'delete'): void {
        const isFavourite = action === 'add';

        // Update main games collection
        games.value.items = games.value.items.map((item) => (item.id === game.id ? { ...item, is_favourite: isFavourite } : item));

        // Update searched games collection
        searchedGames.value.items = searchedGames.value.items.map((item) => (item.id === game.id ? { ...item, is_favourite: isFavourite } : item));

        // Update games by provider collection
        Object.keys(gamesByProvider.value).forEach((provider) => {
            gamesByProvider.value[provider].items = gamesByProvider.value[provider].items.map((item) => (item.id === game.id ? { ...item, is_favourite: isFavourite } : item));
        });
    }
    const awaitGetGames = async (filters: Filters) => {
        const queryString = createQueryString(filters);
        try {
            const headers = isPlayer.value ? getAuthHeaders() : {};
            const response = await axios.get<Items<GameRaw>>(`/main-api/games${isPlayer.value ? '/my' : ''}/filter${queryString}`, headers);
            if (response.status === 200) {
                games.value = {
                    items: response.data.items.map(gameAdapter.mapGame),
                    total: response.data.total,
                    size: response.data.size,
                    page: response.data.page,
                    pages: response.data.pages
                };
            }
        } catch (error) {
            console.error(error);
        }
    };
    const awaitGetRandomGame = async (game: GameRandomURL) => {
        try {
            if (isPlayer.value && isPlayerHaveBalance.value) {
                const response = await axios.get<GameRandomURLResponse>(`/main-api/games/session_url/random/`, {
                    headers: getAuthHeaders().headers,
                    params: game
                });
                if (response.status === 200) {
                    randomGameURL.value = {
                        ...response.data,
                        game: response.data.game ? gameAdapter.mapRandomGame(response.data.game) : null
                    };
                }
            } else {
                const response = await axios.get<RandomGameDetailsRaw>(`/main-api/games/random?currency=${isPlayer.value ? player.value.currency : currenciesStore.currency}`);
                if (response.status === 200) {
                    randomGame.value = gameAdapter.mapRandomGame(response.data);
                }
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitGetGamesBySearch = async (filters: Filters) => {
        const queryString = createQueryString(filters);
        if ((filters.page as number) < 2) {
            loadingSearch.value = true;
        }
        searchFlag.value = true;
        searchName.value = filters.name as string;
        const headers = isPlayer.value ? getAuthHeaders() : {};
        const endpoint = `/main-api/games${isPlayer.value ? '/my' : ''}/filter${queryString}`;
        try {
            const { data, status } = await axios.get<Items<GameRaw>>(endpoint, headers);
            if (status === 200) {
                const rd = {
                    items: data.items.map(gameAdapter.mapGame),
                    total: data.total,
                    size: data.size,
                    page: data.page,
                    pages: data.pages
                };
                if ((filters.page as number) >= 2) {
                    appendItems(searchedGames, rd);
                } else {
                    searchedGames.value = rd;
                }
            }
        } catch (error) {
            console.error(error);
        }
        loadingSearch.value = false;
    };
    const awaitGetGamesByProvider = async (filters: Filters) => {
        const provider = filters.provider as string;
        const queryString = createQueryString(filters);
        const headers = isPlayer.value ? getAuthHeaders() : {};
        const endpoint = `/main-api/games${isPlayer.value ? '/my' : ''}/filter${queryString}`;
        try {
            const response = await axios.get<Items<GameRaw>>(endpoint, headers);
            if (response.status === 200) {
                const gamesData = {
                    items: response.data.items.map(gameAdapter.mapGame),
                    total: response.data.total,
                    size: response.data.size,
                    page: response.data.page,
                    pages: response.data.pages
                };
                if (provider !== undefined) {
                    if (filters.page >= 2) {
                        appendItems(gamesByProvider, gamesData, provider);
                    } else if (filters.page === 1) {
                        gamesByProvider.value[provider] = gamesData;
                    }
                } else {
                    console.warn('cant find provider');
                }
            }
        } catch (error) {
            console.error(error);
        }
    };
    const awaitGetGameURL = async (game: GameURL) => {
        const headers = {
            Authorization: `Bearer ${localStorage.getItem('jwt')}`
        };
        const params = {
            language: game.language,
            demo_flag: game.demo_flag,
            client_type: game.client_type
        };
        try {
            const response = await axios.get<GameURLResponse>(`/main-api/games/session_url/${game.game_string_id}/`, {
                headers: headers,
                params: params
            });
            if (response.status === 200) {
                gameURL.value = response.data;
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    return {
        awaitGetRandomGame,
        randomGameURL,
        randomGame,
        gameURL,
        toUpdateGameFavoriteStatus,
        searchedGames,
        awaitGetGamesBySearch,
        awaitGetGameURL,
        awaitGetGames,
        loadingSearch,
        toResetSearchFlag,
        searchFlag,
        searchName,
        awaitGetGamesByProvider,
        games,
        gamesByProvider
    };
});
